import React, { useState, useRef } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Box,
  Paper,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';

const getDifficultyColor = (level) => {
  switch (level.toLowerCase()) {
    case 'easy': return 'success';
    case 'medium': return 'warning';
    case 'hard': return 'error';
    default: return 'default';
  }
};

const PrintableTaskAnalysis = React.forwardRef(({ taskAnalysis }, ref) => (
  <div ref={ref}>
    <Typography variant="h5" gutterBottom>
      Task Analysis for: {taskAnalysis.taskName}
    </Typography>
    <Typography variant="h6" gutterBottom>
      Materials:
    </Typography>
    <ul>
      {taskAnalysis.materials.map((material, index) => (
        <li key={index}>{material}</li>
      ))}
    </ul>
    <Typography variant="h6" gutterBottom>
      Prerequisites:
    </Typography>
    <ul>
      {taskAnalysis.prerequisites.map((prerequisite, index) => (
        <li key={index}>{prerequisite}</li>
      ))}
    </ul>
    <Typography variant="h6" gutterBottom>
      Steps:
    </Typography>
    <List>
      {taskAnalysis.steps.map((step, index) => (
        <ListItem key={index} sx={{ 
          border: 1, 
          borderColor: 'primary.main',
          borderRadius: 1,
          mb: 2 
        }}>
          <ListItemText
            primary={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {step.stepNumber}. {step.description}
                </Typography>
                <Chip 
                  label={step.difficultyLevel} 
                  color={getDifficultyColor(step.difficultyLevel)}
                  size="small"
                />
              </Box>
            }
            secondary={
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Tip:</strong> {step.tips}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  </div>
));

const TaskAnalysisBuilder = () => {
  const [task, setTask] = useState('');
  const [context, setContext] = useState('');
  const [taskAnalysis, setTaskAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const printableRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
  });

  const generateTaskAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/generateTaskAnalysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ task, context }),
      });
      if (!response.ok) {
        throw new Error('Failed to generate task analysis');
      }
      const data = await response.json();
      setTaskAnalysis(data);
    } catch (error) {
      console.error('Error generating task analysis:', error);
      setError('Failed to generate task analysis. Please try again.');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: 4, backgroundColor: 'white' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Enter a task or skill to analyze:
          </Typography>
          <TextField
            fullWidth
            value={task}
            onChange={(e) => setTask(e.target.value)}
            placeholder="e.g., Brushing teeth"
            variant="outlined"
            sx={{ 
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'primary.main',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.dark',
                },
              },
              mb: 2
            }}
          />
          <Typography variant="h6" gutterBottom>
            Additional context (optional):
          </Typography>
          <TextField
            fullWidth
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g., The learner has fine motor skill difficulties"
            variant="outlined"
            multiline
            rows={2}
            sx={{ 
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'primary.main',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.dark',
                },
              },
              mb: 2
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={generateTaskAnalysis}
            disabled={loading || !task}
            sx={{ mt: 2 }}
          >
            Generate Task Analysis
          </Button>
        </Box>
        {loading && <CircularProgress />}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        {taskAnalysis && (
          <>
            <Box sx={{ display: 'none' }}>
              <PrintableTaskAnalysis ref={printableRef} taskAnalysis={taskAnalysis} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePrint}
              >
                Export to PDF
              </Button>
            </Box>
            <Box>
              <Typography variant="h5" gutterBottom>
                Task Analysis for: {taskAnalysis.taskName}
              </Typography>
              <Typography variant="h6" gutterBottom>
                Materials:
              </Typography>
              <ul>
                {taskAnalysis.materials.map((material, index) => (
                  <li key={index}>{material}</li>
                ))}
              </ul>
              <Typography variant="h6" gutterBottom>
                Prerequisites:
              </Typography>
              <ul>
                {taskAnalysis.prerequisites.map((prerequisite, index) => (
                  <li key={index}>{prerequisite}</li>
                ))}
              </ul>
              <Typography variant="h6" gutterBottom>
                Steps:
              </Typography>
              <List>
                {taskAnalysis.steps.map((step, index) => (
                  <ListItem key={index} sx={{ 
                    border: 1, 
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    mb: 2 
                  }}>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1">
                            {step.stepNumber}. {step.description}
                          </Typography>
                          <Chip 
                            label={step.difficultyLevel} 
                            color={getDifficultyColor(step.difficultyLevel)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Tip:</strong> {step.tips}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default TaskAnalysisBuilder;